import type { AgentBillingAddSitesModalProps } from "@v2/types/modalTypes";

import * as React from "react";

import { SplitModal } from "@replohq/lattice/blocks/modal/SplitModal";
import { skipToken } from "@tanstack/react-query";
import { useCurrentWorkspace } from "@v2/contexts/WorkspaceDashboardContext";
import { analytics } from "@v2/features/infra/analytics/tracking";
import { useModal } from "@v2/store/modalStore";
import { workspaceRouter } from "@v2/trpc/routes/workspace";
import { formatNumber } from "replo-utils/lib/format";
import { pluralize } from "replo-utils/lib/pluralize";

import { showAgentBillingUpdateSuccessToast } from "../agentBillingToasts";
import AddSubscriptionItemContent from "../components/AddSubscriptionItemContent";
import useAgentApplySubscriptionChangeMutation from "../hooks/useAgentApplySubscriptionChangeMutation";
import { useAgentBillingSubscription } from "../hooks/useAgentSubscription";
import { useRateCards } from "../hooks/useRateCards";

const AgentBillingAddSitesModal: React.FC<
  AgentBillingAddSitesModalProps
> = () => {
  const workspace = useCurrentWorkspace();
  const modal = useModal();
  const { data: activeSubscription, currentDraft } =
    useAgentBillingSubscription();
  const { rateCards } = useRateCards();
  const { data: reploSitesUsage } = workspaceRouter.getReploSitesUsage.useQuery(
    workspace?.id ?? skipToken,
  );

  const { startSubscriptionChange, isLoading: isLoadingSubscriptionUpdate } =
    useAgentApplySubscriptionChangeMutation();

  if (
    !workspace ||
    !activeSubscription ||
    !rateCards ||
    !currentDraft ||
    !reploSitesUsage ||
    reploSitesUsage.maximum === null ||
    !activeSubscription.currentPeriodEnd ||
    // (yuxin, 2026-07-14, REPL-29329): custom-contract add-ons go through support.
    activeSubscription.hasCustomPricing
  ) {
    return null;
  }

  const rateCard = rateCards.find(
    (rateCard) => rateCard.plan === activeSubscription.plan,
  );
  const siteProduct = rateCard?.addOnProducts.find(
    (product) => product.featureType === "sites",
  );

  if (!siteProduct) {
    return null;
  }

  const closeModal = () => {
    modal.closeModal({ type: "agentBillingAddSitesModal" });
  };

  return (
    <SplitModal
      open={true}
      onOpenChange={(open) => {
        if (!open) {
          closeModal();
        }
      }}
      media={
        <img
          src="/images/billing/shop-modal-graphic.png"
          alt=""
          className="h-full w-full object-cover"
        />
      }
    >
      <AddSubscriptionItemContent
        currentSubscriptionDraft={currentDraft}
        product={siteProduct}
        currentCount={reploSitesUsage.maximum}
        interval={activeSubscription.interval}
        currentPeriodEnd={activeSubscription.currentPeriodEnd}
        rateCards={rateCards}
        title={`Add additional ${pluralize(siteProduct.displayName, 2)}`}
        onConfirm={({ subscriptionView, additionalCount }) => {
          analytics.logEvent("agentBilling.addSites.submitted", {
            currentPlan: activeSubscription.plan,
            additionalCount,
          });

          startSubscriptionChange(
            {
              workspaceId: workspace.id,
              subscriptionView,
              successUrl: window.location.href,
              cancelUrl: window.location.href,
            },
            {
              onSuccess: (result) => {
                // A bank-approval change isn't applied yet; the approval notice replaces the success toast.
                if (result.confirmationKind !== "bankApproval") {
                  showAgentBillingUpdateSuccessToast(
                    `Added ${formatNumber(additionalCount)} additional ${pluralize(siteProduct.displayName, additionalCount)}`,
                    { showConfetti: true },
                  );
                }
                closeModal();
              },
            },
          );
        }}
        isLoading={isLoadingSubscriptionUpdate}
      />
    </SplitModal>
  );
};

export default AgentBillingAddSitesModal;
